import { Link } from "react-router-dom";
import { Mail, Heart, Download, ArrowRight, FileText, Globe } from "lucide-react";
import { useSEO, publicUrl } from "@/lib/seo";
import { BrandLockup } from "@/components/BrandMark";

/** Press / judges kit — drafted from current product state. */
export default function Press() {
  useSEO({
    title: "Press kit",
    description:
      "SonSpeak press kit: founder story, screenshots, key stats, and contact for journalists, judges, and partners.",
    path: "/press",
  });

  const pressContact = "press@sonspeak.com"; // placeholder until founder confirms
  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <BrandLockup size={180} />
        <a href={`mailto:${pressContact}`} className="pill-btn primary"><Mail size={14} /> Press contact</a>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-12">
        <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">Press kit</div>
        <h1 className="display text-4xl sm:text-6xl font-extrabold leading-tight mb-6">
          SonSpeak. <br />
          <span className="text-[color:var(--coral-deep)]">An AAC app for every family.</span>
        </h1>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">One-line pitch</h2>
          <p className="leading-relaxed">
            SonSpeak is an AAC (Augmentative and Alternative Communication)
            app for nonverbal kids and adults — symbol tiles, real voices,
            real photos, on any tablet.
          </p>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">Why it exists</h2>
          <p className="leading-relaxed">
            AAC apps commonly cost $200–$300 and dedicated speech devices
            cost $3,000–$10,000. SonSpeak is a modern, affordable option
            that gives families, therapists, and schools the same core
            capabilities — built collaboratively with AI engineering tools so
            we can move fast and pass the savings on.
          </p>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">What's in the app</h2>
          <ul className="list-disc pl-5 space-y-1 leading-relaxed">
            <li>Symbol/picture board with text-to-speech, sentence builder, and custom voice recording per tile</li>
            <li>Optional next-word suggestions drawn from the user's own board, plus caregiver-friendly usage summaries</li>
            <li>AI custom symbol generation — bulk-illustrate the whole board in minutes</li>
            <li>Full accessibility suite: switch scanning, dwell-tap, lockdown, modeling mode, high-contrast, reduced motion</li>
            <li>Day/Night auto-switching with bedtime-only vocab and dimmer UI</li>
            <li>Therapist multi-client dashboard, board sharing, batch share, weekly usage summaries, CSV usage snapshots for caregiver conversations</li>
            <li>Multi-language UI (EN/ES/FR), gestalt language processor support, privacy-first design</li>
            <li>PWA with full offline support, Capacitor-ready iOS/Android wrapper</li>
          </ul>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">Pricing</h2>
          <ul className="list-disc pl-5 space-y-1 leading-relaxed">
            <li><strong>Starter — Graduated Boards:</strong> $12/year (boards that graduate 6→12→18 words; add your own voice and photos)</li>
            <li><strong>Membership:</strong> $7/month or $70/year (the complete manual product — unlimited boards, child Board mode, dashboard, professional connections)</li>
            <li>Credits pay for AI work, with posted prices shown before every action</li>
            <li><strong>Schools &amp; clinicians:</strong> we work with them directly — no professional checkout</li>
            <li><strong>Supported access:</strong> case-by-case, email hello@sonspeak.com</li>
            <li>No free tier; early-access families get a clearly communicated grace window when paid plans open.</li>
          </ul>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">How we built it</h2>
          <p className="leading-relaxed">
            One parent, AI as co-builder, no outside funding. The codebase is a
            React PWA + FastAPI + MongoDB stack, deployed on Emergent. The whole
            thing was built and shipped in iterations of focused work between
            childcare, with content moderation, analytics, multi-language UI,
            and accessibility features added without an engineering team behind it.
          </p>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-bold mb-3">Boilerplate</h2>
          <p className="leading-relaxed">
            <strong>SonSpeak</strong> is an affordable AAC app for nonverbal kids
            and adults, built by a parent who couldn't afford the existing $200–$300
            apps or $3,000–$10,000 dedicated devices. It includes AI predictive
            text, caregiver-friendly usage summaries, custom voice recording, full accessibility,
            and offline support. Available at <a href={publicUrl()} className="text-[color:var(--coral-deep)] underline">sonspeak.com</a>.
          </p>
        </section>

        <section className="brutal-card bg-[color:var(--cream-deep)] p-5 grid sm:grid-cols-3 gap-3">
          <a href={`mailto:${pressContact}`} className="pill-btn primary justify-center text-base py-3">
            <Mail size={16} /> {pressContact}
          </a>
          <a href="/sonspeak-fact-sheet.txt" download className="pill-btn justify-center text-base py-3" data-testid="press-fact-sheet">
            <FileText size={16} /> Fact sheet (.txt)
          </a>
          <Link to="/download" className="pill-btn justify-center text-base py-3">
            <Download size={16} /> Try the app
          </Link>
        </section>

        <section className="mt-8 text-sm text-[color:var(--ink-soft)] flex items-center gap-2">
          <Globe size={14} /> All assets and text on this page are free to reproduce with attribution.
          For interview requests, photo of the founder, or higher-res screenshots,
          email <a href={`mailto:${pressContact}`} className="underline">{pressContact}</a>.
          <FileText size={14} />
        </section>
      </main>
    </div>
  );
}
